'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { List, SignIn, SquaresFour, GearSix, CreditCard, SignOut } from '@phosphor-icons/react';
import type { AuthUser } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { V4CtaButton, V4GhostButton } from '@/components/landing/v4-shared';
import { BizRnRLogo } from '@/components/brand/BizRnRLogo';
import { TalkToBriNavChip } from '@/components/voice/TalkToBriNavChip';

interface NavLink {
  href: string;
  label: string;
  isAnchor?: boolean;
}

const navLinks: NavLink[] = [
  { href: '/ai-voice-receptionist', label: 'AI Receptionist' },
  { href: '/ai-business-page', label: 'Business Page' },
  { href: 'https://bizrnr.com/pricing', label: 'Pricing' },
  { href: '/industries', label: 'Industries' },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState<{ email?: string; full_name?: string } | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track which section is currently visible for nav active state.
  // Maintain a Set of currently intersecting sections; activeSection resolves
  // to the first (topmost in document order) that is still visible — or ''
  // when the user is above all sections (hero) or below all of them (footer).
  // Prior implementation only set on intersecting=true and never cleared,
  // causing "Pricing" to stick underlined after scrolling back to hero.
  useEffect(() => {
    if (pathname !== '/') return;
    const sectionIds = ['features', 'how-it-works', 'pricing', 'industries'];
    const visible = new Set<string>();
    const observers: IntersectionObserver[] = [];
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
          const next = sectionIds.find((s) => visible.has(s)) ?? '';
          setActiveSection(next);
        },
        { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [pathname]);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user: u } }: { data: { user: AuthUser | null } }) => {
      if (u) setUser({ email: u.email, full_name: u.user_metadata?.full_name });
    });
  }, []);

  const handleLogout = useCallback(async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push('/auth/login');
    router.refresh();
  }, [router]);

  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const [, hash] = href.split('#');
    if (!hash) return;
    if (pathname === '/') {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileOpen(false);
  }, [pathname]);

  /**
   * Active state logic:
   * - Anchor links (/#features, /#how-it-works) are scroll-to section links.
   *   They do NOT represent a distinct route, so they are NEVER marked active
   *   via pathname matching — doing so causes every anchor link to activate
   *   simultaneously on the homepage.
   * - Page-level routes use exact pathname matching (with optional sub-path support).
   */
  const isActive = (link: NavLink): boolean => {
    if (link.isAnchor) {
      // Match section id from anchor href (e.g. /#features -> 'features')
      const sectionId = link.href.split('#')[1];
      return pathname === '/' && !!sectionId && activeSection === sectionId;
    }
    return pathname === link.href || pathname.startsWith(link.href + '/');
  };

  const userInitial = user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <>
      <style>{`
        .v4-nav-link:hover { color: var(--paper) !important; }
        .v4-cta-primary:focus-visible,
        .v4-cta-ghost:focus-visible { outline: 2px solid var(--cyan-glow); outline-offset: 3px; }
      `}</style>
      <nav
        className={`fixed top-3 left-1/2 -translate-x-1/2 z-50 w-[min(calc(100%-2rem),72rem)] transition-all duration-300 ${
          scrolled ? 'rounded-full shadow-lg shadow-black/20' : 'rounded-full'
        }`}
        style={{
          background: 'linear-gradient(180deg, rgba(5,6,10,0.82), rgba(5,6,10,0.55))',
          backdropFilter: 'blur(16px) saturate(140%)',
          borderBottom: '1px solid var(--hair)',
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Three-column flex: logo (natural) / center nav (flex-1, content-centered) /
              auth actions (natural, right-aligned). Natural flex instead of absolute
              positioning gives the auth group proper breathing room from the center
              links and lets right-justification work correctly. justify-between keeps
              the mobile menu button right-anchored when the desktop sections are hidden. */}
          <div className="flex items-center justify-between h-12 gap-6">
            {/* Logo */}
            <Link href="/" className="flex min-h-[44px] items-center gap-2.5 flex-shrink-0" style={{ minHeight: 44 }}>
              <BizRnRLogo size={28} />
              <span style={{
                fontFamily: 'var(--font-geist-sans)', fontWeight: 700, fontSize: 18,
                letterSpacing: '-0.02em', color: 'var(--paper)',
              }}>BizRnR</span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex flex-1 items-center justify-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={link.isAnchor ? (e) => handleAnchorClick(e, link.href) : undefined}
                  className={`v4-nav-link inline-flex min-h-[44px] items-center gap-1.5 relative ${
                    isActive(link)
                      ? 'after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-0.5 after:bg-[var(--cyan-glow)] after:rounded-full'
                      : ''
                  }`}
                  style={{
                    color: isActive(link) ? 'var(--paper)' : 'var(--dim)',
                    fontSize: '14px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                    minHeight: 44,
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop Auth — natural width, right-anchored via the flex container.
                No flex-1 so it doesn't force the center nav off-center. */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <TalkToBriNavChip />
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-button"
                      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cyan-glow)]"
                      aria-label="User menu"
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarFallback>{userInitial}</AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link href="/dashboard" rel="nofollow" className="flex items-center gap-2">
                        <SquaresFour className="w-4 h-4" weight="duotone" /> Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/dashboard/settings" rel="nofollow" className="flex items-center gap-2">
                        <GearSix className="w-4 h-4" weight="duotone" /> Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/dashboard/settings?tab=billing" rel="nofollow" className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" weight="duotone" /> Billing
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="logout-button"
                      className="flex items-center gap-2 text-error focus:text-error cursor-pointer"
                      onSelect={handleLogout}
                    >
                      <SignOut className="w-4 h-4" weight="duotone" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <V4GhostButton href="/auth/login" variant="paper" size="md">
                    <SignIn className="w-4 h-4" weight="duotone" />
                    Sign In
                  </V4GhostButton>
                  <V4CtaButton href="/auth/register" size="md" className="v4-nav-cta">
                    Get started
                  </V4CtaButton>
                </>
              )}
            </div>

            {/* Mobile Menu — Sheet */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <button
                  className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  style={{ color: 'var(--dim)' }}
                  aria-label="Open menu"
                >
                  <List className="w-5 h-5" weight="bold" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-72"
                style={{ background: 'var(--ink)' }}
                onInteractOutside={() => setMobileOpen(false)}
                onEscapeKeyDown={() => setMobileOpen(false)}
              >
                <SheetHeader style={{ borderColor: 'var(--hair)' }}>
                  <SheetTitle className="text-left" style={{ color: 'var(--paper)' }}>BizRnR</SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={link.isAnchor ? (e) => handleAnchorClick(e, link.href) : () => setMobileOpen(false)}
                      className="flex min-h-[44px] items-center justify-between gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        color: isActive(link) ? 'var(--cyan-glow)' : 'var(--dim)',
                        background: isActive(link) ? 'rgba(125,250,255,0.08)' : 'transparent',
                        minHeight: 44,
                      }}
                    >
                      <span>{link.label}</span>
                    </Link>
                  ))}
                  <div className="pt-4 border-t space-y-2" style={{ borderColor: 'var(--hair)' }}>
                    <TalkToBriNavChip fullWidth onOpen={() => setMobileOpen(false)} />
                    {user ? (
                      <>
                        <V4CtaButton href="/dashboard" className="w-full" size="md">
                          Dashboard
                        </V4CtaButton>
                        <V4GhostButton href="/dashboard/settings" variant="paper" size="md" className="w-full">
                          Settings
                        </V4GhostButton>
                        <button
                          data-testid="mobile-logout-button"
                          className="w-full text-sm font-medium py-2"
                          style={{ color: 'var(--dim)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                          onClick={() => { setMobileOpen(false); handleLogout(); }}
                        >
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <V4CtaButton href="/auth/register" size="md" className="w-full">
                          Get started
                        </V4CtaButton>
                        <V4GhostButton href="/auth/login" variant="paper" size="md" className="w-full">
                          Sign In
                        </V4GhostButton>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navbar;
